import React, { useState, useEffect, useRef } from "react";
import { useApolloClient } from "@apollo/client";
import { toast } from "react-toastify";

import useSettings from "../hooks/settings";
import { OrderType } from "../models/order_model";
import { CashoutsType, CashMoveType } from "../models/cashouts";
import {
  getTicket,
  getCashcutTicket,
  getOpenDrawerRaw,
  PrintType,
} from "./printer/getTicket";
import { GET_PRINTERS } from "../hooks/queries";

const testing = false; /// set to true for testing in console and false to print on real paper
const service_to_find = "e7810a71-73ae-499d-8c15-faa9aef0c3f2";
const characteristic_id = "bef8d6c9-9c21-4c9e-b632-bd58c1009f9f";
const device_fiters = {
  vendorId: 1046,
  services: [service_to_find],
};

type GetFiltersFun = () => USBDeviceRequestOptions | undefined;
type PrinterContextType = {
  connected: boolean;
  errors: any;
  connect: () => Promise<boolean | void>;
  print: (order_data: OrderType, type: PrintType, re_print?: boolean) => void;
  printCashcut: (
    cashouts: CashoutsType,
    moves: CashMoveType[],
    date: string,
  ) => void;
  printRaw: (data: { print_text: Uint8Array; has_products: boolean }) => void;
  openDrawer: () => void;
};

export const PrinterContext = React.createContext({
  connected: false,
  errors: false,
  connect: async () => {},
  print: async () => {},
  printCashcut: async () => {},
  printRaw: async () => {},
  openDrawer: async () => {},
} as PrinterContextType);

const PrinterContextProvider: React.FC = (props) => {
  const client = useApolloClient();
  const [settings, saveSettings] = useSettings();
  const [printer, setPrinter] = useState<
    USBDevice | BluetoothRemoteGATTCharacteristic | boolean
  >(testing);
  const [device_errors, setDevice_errors] = useState(null);

  const promtPrint = async (
    order_data: OrderType = {} as OrderType,
    type: PrintType = "client",
    re_print: boolean = false,
  ) => {
    if (order_data.products) {
      await print(order_data, type, re_print);
    }
  };

  const print = async (
    order_data: OrderType,
    type: PrintType = "client",
    re_print: boolean = false,
  ) => {
    if (
      !printer &&
      !settings.print_console &&
      settings.printer_type !== "network"
    ) {
      await connectDevice();
    }
    //TODO:count duplicates
    await getTicketFun(order_data, type, re_print);
  };

  // const countSimilarProducts = (order_data: OrderType) => {
  //   let ch_text_active = "";
  //   let ch_count_active = 0;
  //   let past_product: OrderProduct;
  //   console.log("printer-context.tsx:85 | order_data", order_data);
  //   const new_products: OrderProduct[] = [];
  //   order_data.products
  //     .sort((a, b) =>
  //       parseInt(a.product.id) > parseInt(b.product.id) ? 1 : -1
  //     )
  //     .forEach((product) => {
  //       let ch_text: string = `${product.product.id.toLocaleString()}${
  //         product.ingredients_txt
  //       }${product.price.toLocaleString()}`;
  //       if (product.comments) {
  //         ch_text += product.comments;
  //       }
  //       ch_count_active += product.qty;
  //       if (ch_text === ch_text_active) {
  //         return;
  //       }
  //       ch_text_active = ch_text;
  //       if (past_product && past_product.product.id !== product.product.id) {
  //       }
  //       product.qty = ch_count_active;
  //       past_product = product;
  //       ch_count_active = 0;
  //       new_products.push(product);
  //     });
  //   order_data.products = new_products;
  //   getTicketFun(order_data);
  // };

  const promtPrintCashcut = async (
    cashouts: CashoutsType,
    moves: CashMoveType[],
    date: string,
  ) => {
    if (
      !printer &&
      !settings.print_console &&
      settings.printer_type !== "network"
    ) {
      await connectDevice();
    }
    const ticket = getCashcutTicket(cashouts, moves, settings, date);
    if (settings.print_console) {
      console.log(
        "printer-context.tsx | cashcut ticket",
        Buffer.from(ticket.print_text).toString(),
      );
    } else {
      await printTicket(ticket);
    }
  };

  const promtOpenDrawer = async () => {
    if (!settings.open_drawer) return;
    if (
      !printer &&
      !settings.print_console &&
      settings.printer_type !== "network"
    ) {
      await connectDevice();
    }
    const ticket = getOpenDrawerRaw(settings);
    if (settings.print_console) {
      console.log(
        "printer-context.tsx | open drawer",
        Buffer.from(ticket.print_text).toString(),
      );
    } else {
      await printTicket(ticket);
    }
  };

  const getTicketFun = async (
    order_data: OrderType,
    type: PrintType = "client",
    re_print: boolean = false,
  ) => {
    const get_ticket = await getTicket(order_data, settings, type, re_print);
    if (settings.print_console) {
      console.log(
        "printer-context.tsx:98 | get_ticket",
        Buffer.from(get_ticket.print_text).toString(),
      );
    } else {
      await printTicket(get_ticket);
    }
  };

  const printTicket = (ticket: any) => {
    console.log("printer-context.tsx:108 | ticket", ticket);
    if (ticket.has_products === false) return false;
    if (settings.printer_type === "network") {
      return sendToNetwork(ticket);
    }
    if (!printer || printer === true) {
      notifyNoPrinterAttached();
      return false;
    }
    return new Promise(async (resolve, reject) => {
      if (!("TextEncoder" in window)) {
        alert("Sorry, this browser does not support TextEncoder...");
        return resolve(false);
      }
      if (settings.printer_type === "bluetooth" && "writeValue" in printer) {
        const MAX_DATA_SEND_SIZE = 20;
        let chunkCount = Math.ceil(
          ticket.print_text.byteLength / MAX_DATA_SEND_SIZE,
        );
        let index = 0;

        while (chunkCount > 0) {
          const chunk = ticket.print_text.slice(
            index,
            index + MAX_DATA_SEND_SIZE,
          );
          index += MAX_DATA_SEND_SIZE;
          chunkCount--;
          await printer.writeValue(chunk);
        }
      } else if ("configuration" in printer) {
        let endpointNumber;
        if (
          printer.configuration?.interfaces[0].alternate.endpoints[0]
            .direction === "out"
        ) {
          endpointNumber =
            printer.configuration.interfaces[0].alternate.endpoints[0]
              .endpointNumber || 1;
        } else {
          endpointNumber =
            printer.configuration?.interfaces[0].alternate.endpoints[1]
              .endpointNumber || 1;
        }
        printer
          .transferOut(endpointNumber, ticket.print_text)
          .then(() => {
            return resolve(true);
          })
          .catch((error: any) => {
            console.log("main_partner.js:2445 | error", error);
            return resolve(false);
          });
      }

      return resolve(false);
    });
  };

  // const printBluetooth = () => {
  //   return new Promise((resolve, reject) => {
  //     let chain = Promise.resolve();

  //     for (let i of ticket.print_text) {
  //       console.log("printer-context.js:226 | i", i);
  //       chain = chain.then(() => {
  //         return printer.writeValue(i).then(() => {
  //           return true;
  //         });
  //       });
  //     }
  //     resolve(chain);
  //   });
  // };

  const sendToNetwork = async (ticket: any) => {
    try {
      const branch_id = settings.active_branch
        ? +settings.active_branch
        : undefined;
      const { data: printersData } = await client.query({
        query: GET_PRINTERS,
        variables: { branch_id },
        fetchPolicy: "network-only",
      });
      const printers = printersData?.getPrinters;
      if (!printers || printers.length === 0) {
        console.error("No network printers configured");
        return false;
      }
      const printer = printers[0];
      const port = printer.port || "9100";
      const url = `http://${printer.ip_address}:${port}`;
      const blob = new Blob([ticket.print_text], {
        type: "application/octet-stream",
      });
      await fetch(url, {
        method: "POST",
        body: blob,
        mode: "no-cors",
      });
      return true;
    } catch (error) {
      console.error("Network print error:", error);
      return false;
    }
  };

  // Shared by both the gesture-triggered pairing flow (requestDevice, shows
  // the browser's picker) and the silent reattach-on-load flow (getDevices,
  // no picker) — everything past "we have a BluetoothDevice" is identical.
  const attachBluetoothDevice = async (device: BluetoothDevice) => {
    device.addEventListener("gattserverdisconnected", printer_disconnected);
    if (!device.gatt) return false;
    const server = await device.gatt.connect();
    const service = await server.getPrimaryService(service_to_find);
    const characteristic = await service.getCharacteristic(characteristic_id);
    setPrinter(characteristic);
    return true;
  };

  const connectBluetooth = async () => {
    try {
      const device = await navigator.bluetooth.requestDevice({
        filters: [device_fiters],
        // acceptAllPrinter: true,
        // optionalServices: [service_to_find]
      });
      if (!device) return false;
      return await attachBluetoothDevice(device);
    } catch (error: any) {
      setDevice_errors({ ...error });
      notifyConnectFailure(error);
      return false;
    }
  };

  // Same split as attachBluetoothDevice: shared "claim the USB device" steps
  // for both the gesture-triggered (requestDevice) and silent reattach
  // (getDevices) flows.
  const attachUSBDevice = async (device: USBDevice) => {
    saveSettings("device_vendor_id", device.vendorId);
    navigator.usb.addEventListener("disconnect", printer_disconnected);
    await device.open(); // Begin a session.
    await device.selectConfiguration(1);
    await device.claimInterface(0);
    setPrinter(device);
    return true;
  };

  const connectUSB = async () => {
    try {
      const filters_var = get_filters_var();
      const device = await navigator.usb.requestDevice(filters_var);
      return await attachUSBDevice(device);
    } catch (error: any) {
      setDevice_errors({ ...error });
      notifyConnectFailure(error);
      ///delete previous USB Vendor ID
      saveSettings("printer_vendor_id", "");
      return false;
    }
  };

  // Reattach to a printer the browser already granted permission for in a
  // previous session, without needing a fresh click. Both getDevices() calls
  // are gesture-free by design, unlike requestDevice(). No-op (and no toast)
  // when there's nothing to reattach to — that's the common case right after
  // a fresh pairing grant, not a failure.
  const reattachDevice = async () => {
    try {
      if (settings.printer_type === "bluetooth") {
        if (!navigator.bluetooth?.getDevices) return;
        const devices = await navigator.bluetooth.getDevices();
        const device = devices[0];
        if (device) await attachBluetoothDevice(device);
      } else {
        if (!navigator.usb?.getDevices) return;
        const devices = await navigator.usb.getDevices();
        const device = devices[0];
        if (device) await attachUSBDevice(device);
      }
    } catch (error: any) {
      // Reattach is best-effort — fall back to the normal gesture-gated
      // connect the next time something tries to print, silently.
      console.log("printer-context.tsx | reattach error", error);
    }
  };

  const notifiedErrorsRef = useRef<Set<string>>(new Set());

  // Surface a connect failure to staff instead of it failing invisibly (the
  // previous behavior: printTicket would just no-op). Most common cause is
  // Chrome refusing requestDevice() because the call wasn't triggered by a
  // click (e.g. an auto-print from the order subscription) — dedupe by
  // error name per session so this doesn't spam a toast on every order.
  const notifyConnectFailure = (error: any) => {
    const key = `${settings.printer_type}:${error?.name || "unknown"}`;
    if (notifiedErrorsRef.current.has(key)) return;
    notifiedErrorsRef.current.add(key);
    if (error?.name === "NotFoundError") {
      // User closed the device picker or has no printer paired yet — not
      // worth alarming staff about on every auto-print attempt.
      return;
    }
    toast.error(
      "No se pudo conectar con la impresora. Imprime un ticket de prueba desde Ajustes > Tickets para volver a conectarla.",
    );
  };

  // Auto-print (subscription-triggered) tried to print but no USB/Bluetooth
  // printer is attached and reattachDevice() found nothing to silently
  // resume — this used to fail with no signal to staff at all. Once per
  // session is enough; the fix (pair via a manual print) is the same every
  // time, and this fires on every incoming order until someone does that.
  const noPrinterNotifiedRef = useRef(false);
  const notifyNoPrinterAttached = () => {
    if (noPrinterNotifiedRef.current) return;
    noPrinterNotifiedRef.current = true;
    toast.warn(
      "La impresora no está conectada — los tickets no se imprimirán automáticamente. Imprime un ticket de prueba desde Ajustes > Tickets para conectarla.",
    );
  };

  // On session start, try to silently reattach to a previously-paired
  // USB/Bluetooth printer so auto-print works right away instead of only
  // after someone clicks a print button once. Network printers use plain
  // fetch() (no pairing/gesture involved) and print_console is a
  // developer-only testing mode, so neither needs this.
  useEffect(() => {
    if (settings.printer_type === "network" || settings.print_console) return;
    if (printer) return;
    reattachDevice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.printer_type, settings.print_console]);

  const get_filters_var: GetFiltersFun = () => {
    const filters_var: USBDeviceRequestOptions = {
      filters: [],
    };
    if (settings.printer_vendor_id) {
      filters_var.filters.push({
        vendorId: settings.printer_vendor_id,
        //@ts-ignore next line
        services: [service_to_find],
      });
    } else {
      // filters_var.filters.push({ interfaceClass: 7 });
      // filters_var.acceptAllDevices: true,
      // filters_var.optionalServices: [service_to_find],
    }

    return filters_var;
  };

  const promtPrintRaw = async (data: {
    print_text: Uint8Array;
    has_products: boolean;
  }) => {
    if (
      !printer &&
      !settings.print_console &&
      settings.printer_type !== "network"
    ) {
      await connectDevice();
    }
    if (settings.print_console) {
      console.log(
        "printer-context.tsx | raw print",
        Buffer.from(data.print_text).toString(),
      );
    } else {
      await printTicket(data);
    }
  };

  const connectDevice = async () => {
    if (settings.printer_type === "network") {
      return;
    }
    if (settings.printer_type === "bluetooth") {
      return await connectBluetooth();
    }
    ///usb default
    return await connectUSB();
  };

  const printer_disconnected = () => {
    setPrinter(false);
    // Allow the "printer not connected" warning to fire again for this new
    // disconnected stretch, rather than staying suppressed for the rest of
    // the session after the first time it was shown.
    noPrinterNotifiedRef.current = false;
  };

  return (
    <PrinterContext.Provider
      value={{
        connect: connectDevice,
        print: promtPrint,
        printCashcut: promtPrintCashcut,
        printRaw: promtPrintRaw,
        openDrawer: promtOpenDrawer,
        errors: device_errors,
        connected: printer ? true : false,
      }}
    >
      {props.children}
    </PrinterContext.Provider>
  );
};

export default PrinterContextProvider;
